/* eslint-disable max-lines */

import type { PaginationMeta, SortParams } from '@core/http-types';
import type { SupportedTranslations } from '@core/i18n-legacy';
import { TellentReportsEnvironment } from '@core/reports-types';

import { TellentSubscription } from './billing/subscription.types';
import type { TellentFeatureDiscoveryFeatures } from './feature-discovery/feature-discovery.types';

export enum SeatKind {
  Recruitment = 'recruitment',
  CoreHR = 'core_hr',
  Grow = 'grow',
}

export type SeatKindLegacy = Exclude<SeatKind, SeatKind.Grow>;

type AppBase = {
  kind: SeatKind;
  guid: string;
  orgGuid: string;
  externalId?: string;
  data: {};
  subscriptionItems: Array<{ id: string; type: string }>;
};

export type AppRecruitment = AppBase & {
  kind: SeatKind.Recruitment;
  data: {
    careersSite?: { enabled: boolean; url: string };
    referralProgram?: { enabled: boolean; url: string };
  };
};

export type AppCoreHr = AppBase & {
  kind: SeatKind.CoreHR;
  data: {};
};

export type App = AppRecruitment | AppCoreHr;

export type Seat = {
  access: boolean;
  status: string; // 'active'
  app: App;
  role: Role | null;
  guid: string;
  locale: string;
  appGuid: string;
  memberGuid: string;
  roleExternalId: null;
};

export type Role = {
  abilities: string[];
  admin: boolean;
  appGuid: string;
  externalId: string;
  id: number;
  name: string;
};

export enum OrgStatus {
  Blocked = 'blocked',
  Active = 'active',
  DeletionRequested = 'deletion_requested',
}

export type OrgDeletionStatus = {
  canceledAt: string;
  orgGuid: string;
  processAt: string;
  requester: {
    name: string;
  };
  userGuid: string;
};

export interface TellentOrgAgreementPayload {
  html: string;
  kind: string;
}

export interface TellentOrgAgreement {
  html: string;
  kind: string;
  orgGuid: string;
  userGuid: string;
  createdAt: string;
  updatedAt: string;
}

export interface TellentOrgAgreementCheck {
  agreed: boolean;
}

export type OrgFeatures =
  | 'sso'
  | 'grow'
  | 'ai_writer'
  | 'ai_job_content'
  | 'ai_translate'
  | 'evaluation_insights'
  | 'hr_assistant'
  | 'ai_candidate_screening';

export type Org = {
  name: string;
  guid: string;
  language?: string;
  status: OrgStatus;
  deletionStatus?: OrgDeletionStatus;
  signupDomainVerified: boolean;
  oneBillingEnabled: boolean;
  sympaDeal: boolean;
  features: OrgFeatures[];
  membersLimit: number | null;
  canCreateOrg: boolean;
  logo?: File | null | string;
  logoThumbUrl?: string | null;
};

export type NewOrgResponse = {
  guid: string;
  language: SupportedLanguages;
  name: string;
  signup_domain_verified: boolean;
};

export type SupportedLanguages = 'nl' | 'en' | 'de' | 'fr';

export type AddOrgPayload = {
  name: string;
  language: SupportedLanguages;
  apps: SeatKind[];
  sampleData: boolean;
};

export enum TellentOrgAbilities {
  ManageTellent = 'manage_tellent',
  ManageUsers = 'manage_users',
  ManageBilling = 'manage_billing',
}

export enum TellentOrgAbilitiesExtended {
  ManageBilling = 'manage_billing',
  RecruitmentViewAllJobs = 'recruitment_view_all_jobs',
}

export type TellentMember = {
  owner: boolean;
  status: TeamMemberStatus;
  org: Org;
  firstName: string;
  lastName: string;
  email: string;
  seats: Seat[];
  guid: string;
  userGuid: string;
  orgGuid: string;
  abilities: Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>;
  invitation: {
    accepted: boolean;
    id: number;
  } | null;
  teams: Array<{ name: string; guid: string; assignedAt: string; kind: TellentGroupKind }>;
  avatarThumbUrl: string;
  '2fa': boolean;
  extraPayload?: any;
  personalEmail?: string;
  hasWorkEmail?: boolean;
  login?: string;
  hasTemporaryPassword?: boolean;
};

export enum TeamMemberStatus {
  Active = 'active',
  Invited = 'invited',
  NotInvited = 'notInvited',
  Deactivated = 'deactivated',
  TemporaryPassword = 'temporaryPassword',
  PausedAccount = 'pausedAccount',
}

export type TellentMemberRow = {
  id: string;
  owner: boolean;
  status: TeamMemberStatus;
  org: Org;
  firstName: string;
  lastName: string;
  email: string;
  seats: Seat[];
  guid: string;
  userGuid: string | null;
  orgGuid: string;
  abilities: Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>;
  invitation: {
    accepted: boolean;
    id: number;
  } | null;
  avatarThumbUrl: string;
  '2fa': boolean;
  recruitment?: string;
  coreHr?: string;
};

export type MemberEmailContext = {
  hasWorkEmail: boolean;
  hasPersonalEmail: boolean;
  loginMethod: 'email' | 'username';
};

export type InviteMemberPayload = {
  members: Array<Pick<TellentMember, 'email' | 'firstName' | 'lastName' | 'extraPayload'>>;
  seats: Array<Pick<Seat, 'appGuid' | 'access' | 'roleExternalId'>>;
  sendInvitation: boolean;
};

export type InviteMemberPayloadNew = {
  members: Array<
    Pick<TellentMember, 'email' | 'firstName' | 'lastName' | 'login' | 'extraPayload'>
  >;
  seats: Array<Pick<Seat, 'appGuid' | 'access' | 'roleExternalId'>>;
  sendInvitation: boolean;
  generateLogin: boolean;
};

export type TellentMembersResponse = {
  data: TellentMember[];
  meta: PaginationMeta;
};

export type TellentMemberResponse = {
  data: TellentMember;
};

export interface TellentCurrentUser {
  email: string;
  firstName: string;
  lastName: string;
  members: TellentMember[];
  guid: string;
  hasWorkEmail: boolean;
  personalEmail?: string;
  recoveryEmail?: string;
  login?: string;
  identityHash: string;
  socketToken: string;
  language?: string; // missing in the response
  timezone?: string;
  timeFormat?: string;
  phone?: string;
  locale: SupportedTranslations;
  weekStartDay?: string;
  appLanguages?: AppLanguages;
  avatarThumbUrl: string;
  currentOrgGuid: string;
  featureDiscovery: TellentFeatureDiscoveryFeatures;
  isAuthorizedWith: 'email' | 'sso';
  flags: {
    grow: boolean;
  };
}

export interface AppLanguages {
  recruitment?: SupportedTranslations;
  coreHr?: SupportedTranslations;
  javelo?: SupportedTranslations;
}

export interface TellentCurrentUserUpdate {
  firstName: string;
  lastName: string;
  email: string;
  recoveryEmail?: string;
  login?: string;
  code?: string;
  avatar?: File;
  language?: string;
  timezone?: string;
  timeFormat?: string;
  phone?: string;
  weekStartDay?: string;
  recruiteeLanguage?: string;
  kiwiLanguage?: string;
}

export interface TellentUserResponse {
  user: TellentCurrentUser;
  accessToken: {
    expiresAt: string;
    kind: 'email' | 'sso';
    orgGuid: string | null;
    userGuid: string;
  };
}

export type TellentRole = {
  name: string;
  admin: boolean;
  externalId: string;
  default: boolean;
  abilities?: Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>;
};

export type TellentPasswordChange = {
  password: string;
  passwordConfirmation: string;
  code?: string;
  currentPassword?: string;
};

export type TellentPasswordChangeResponse = {
  success: boolean;
};

export interface TellentCommonEnvironment {
  tellentApiUrl: string;
  tellentApiWsUrl: string;
  tellentAuthUrl: string;
  tellentAppUrl: string;
  tellentOneDomainUrl: string;
}

// Common interface for all tellent apps
export interface TellentAppCommonConfig extends TellentCommonEnvironment {
  tellentAuthTokenUrl: string;
  tellentUserUrl: string;
  recruiteeWebAppUrl?: string;
  headers: {};
}

export interface TellentAdminAppConfig extends TellentAppCommonConfig {}

export interface TellentClientAppConfig extends TellentAppCommonConfig, TellentReportsEnvironment {}

export type TellentAuthTokenResponse = {
  accessToken: string;
  expiresAt: string;
  expiresIn: number;
  orgGuid: string | null;
  userGuid: string;
};

export type TeamMembersSortBy = 'name' | 'status' | 'recruitment';

export interface TeamMembersPaginationParams {
  guids?: string[];
  page?: number;
  limit?: number;
  q?: string;
  statuses?: TeamMembersStatuses[];
  scope: string;
  sortBy?: TeamMembersSortBy;
  sortOrder?: 'asc' | 'desc';
}
export type TeamMemberPaginationWithSortParams = TeamMembersPaginationParams & SortParams;

export interface TeamMembersSortParams extends SortParams {
  sortBy?: TeamMembersSortBy;
}

export type TeamMembersParams = TeamMemberPaginationWithSortParams & {
  sortBy?: TeamMembersSortBy;
  scope: string;
};

export enum TeamMembersStatuses {
  Active = 'active',
  Deactivated = 'deactivated',
}

export type TellentAppBilling = {
  addons: string[];
  manual: boolean;
  status: 'trialing' | 'active' | 'non_renewing' | 'future' | 'paused' | 'blocked' | 'inactive';
  displayStatus: 'active' | 'non_renewing' | 'blocked' | 'inactive' | 'trialing';
  periodEnd?: string;
};

export type TellentApps = {
  status: string;
  kind: SeatKind;
  guid: string;
  roles: TellentRole[];
  billing: null | TellentAppBilling;
  subscription: null | TellentSubscription;
};

export type CreateTellentApp = {
  kind: SeatKind;
  sampleData: boolean;
  signUpInfo: {
    recruitmentInvolvement?: string;
  };
};

export type TellentAppsResponse = {
  data: TellentApps[];
};

export type ManageSeatsResponse = {
  data: [
    {
      access: boolean;
      appGuid: string;
      guid: string;
      roleExternalId: string;
      status: string;
    },
  ];
};

export enum AppKind {
  KiwiHr = 'core_hr',
  Recruitee = 'recruitment',
  TellentAdmin = 'admin',
  Javelo = 'grow',
}

export enum TellentBrand {
  Tellent = 'tellent',
  Recruitee = 'recruitee',
  Kiwi = 'kiwi',
  Javelo = 'javelo',
}

export type TellentAppsBrand = 'kiwi' | 'javelo' | 'recruitee' | 'tellent';

export interface MemberDataUpdate {
  firstName?: string;
  lastName?: string;
  avatar?: File;
  email?: string;
  login?: string;
  guid: string;
}

export interface SignInConsent {
  createdAt: string;
  expiresAt: string;
  id: number;
  revokedAt: string | null;
  status: SignInConsentStatus;
}

export enum SignInConsentStatus {
  Active = 'active',
  Revoked = 'revoked',
  Expired = 'expired',
}

export interface SignInConsentResponse {
  data: SignInConsent[];
  meta: PaginationMeta;
}

export interface TellentIAppConfig {
  tellentApiUrl: string;
  tellentAppUrl: string;
  tellentAuthTokenUrl: string;
  tellentAuthUrl: string;
  tellentUserUrl: string;
}

export enum UserProfileTab {
  Profile = 'profile',
  Companies = 'companies',
  Security = 'security',
}

export enum MemberEditTab {
  Account = 'user_details',
  EditProfile = 'edit_current_user_profile',
  Groups = 'groups',
  Departments = 'departments',
  PermissionsAccess = 'permissions_and_access',
}

export enum TellentGroupKind {
  Department = 'department',
  Group = 'group',
}

export type TellentGroupTeamMember = {
  guid: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarThumbUrl: string;
  assignedAt: string;
};

export type TellentGroupTranslations = Record<string, { name: string }>;

export type TellentGroup = {
  id: string;
  guid: string;
  membersCount: number;
  members?: TellentGroupTeamMember[];
  translations: TellentGroupTranslations;
  name: string;
  kind: TellentGroupKind;
};
